import { BaseResponseType2 } from '../cognito/authResponse.types';
import { RecordStatus } from '../menu/enums.types';

export const otherReviewTopic = 'Others';

export type ReviewAccountSource = ReviewSource.GOOGLE | ReviewSource.YELP;

export type ReviewSqsSource = ReviewSource.GOOGLE | ReviewSource.TRIPADVISOR | 'Serp';

export enum ReviewSource {
  GOOGLE = 'Google',
  YELP = 'Yelp',
  TRIPADVISOR = 'Tripadvisor',
  INTERNAL = 'AIO Internal',
  OPEN_TABLE = 'OpenTable',
  NUGGET = 'Nugget',
  ONLINE = 'Online',
}

export type ArmEnqueue = {
  messageBody: MessageBody;
};

export type ArmStoreReviews = {
  messageBody: {
    restaurantId: number;
    businessId: number;
    source?: string;
    reviews: IReviewResponse[];
  };
};

export type MessageBody = {
  restaurantId: number;
  businessId: number;
  source: ReviewSource | 'Serp';
  serpSource?: ReviewAccountSource;
};

export enum ReviewSentiment {
  POSITIVE = 'Positive',
  NEUTRALS = 'Neutral',
  NEGATIVE = 'Negative',
  MIXED = 'Mixed',
}

export enum ReviewAspectSentiment {
  POSITIVE = 'Positive',
  NEUTRALS = 'Neutral',
  MIXED = 'Mixed',
  NEGATIVE = 'Negative',
}

export enum ReviewAspect {
  STAFF = 'Staff Service and Behavior',
  VALUE_FOR_MONEY = 'Price and Value for Money',
  FOOD = 'Food Quality',
  AMBIENCE = 'Ambience',
  OTHER = 'Other',
}

export enum ReviewFeedbacks {
  POSITIVE = 'Positive Only',
  NEGATIVE = 'Negative Only',
  MIXED = 'Mixed Only',
  NEUTRALS = 'Neutrals Only',
  NOT_RESPONDED = 'Not Responded',
}

export enum ReviewReplyStatus {
  REPLIED = 'Replied',
  NOT_REPLIED = 'Not replied',
  UNREAD = 'Unread',
  READ = 'Read',
}

export enum ReviewReplyTypes {
  SHORT = 'short',
  LONG = 'long',
}

export enum ReviewDurations {
  ALL_TIME = 'All Time',
  MONTH = 'Last Month',
  HALF_YEAR = 'Last 6 Months',
  YEAR = 'Last 12 Months',
}

export enum RepliedStatusFlag {
  REPLIED = 'REPLIED',
  NOT_REPLIED = 'NOT_REPLIED',
}

export enum ReviewAccountSearch {
  RESTAURANT_NAME = ' Restaurant name',
  RESTAURANT_ID = ' Restaurant ID',
}

export interface ISourcesOverview {
  source: ReviewSource;
  rating: number;
  count: number;
  rate: 1;
}

export interface IRatingsOverview {
  rating: number;
  count: number;
  percentage: number;
}

export interface IAspectsOverview {
  aspect: ReviewAspect;
  rating: number;
  count: number;
}

export interface IReviewOverview {
  total: number;
  average: number;
  ratings: IRatingsOverview[];
  sources: ISourcesOverview[];
  aspects: IAspectsOverview[];
}

export interface ISentimentOverview {
  count: number;
  sentiment: ReviewSentiment;
  percentage: number;
  review_count: number;
  source_breakdown: Record<string, number>;
}

export interface IAspectGrouped extends Omit<ISentimentOverview, 'percentage'> {
  aspect: ReviewAspect;
}

export interface IReviewSummary {
  id: number;
  sentiment: ReviewSentiment;
  summary: string;
  keywords: string[];
  ranking: number;
  createdAt: Date;
}

interface OneLinerKeywords {
  keywordName: string;
  sentiment: ReviewSentiment;
  occurrence: number;
  start_index: number | null;
  suggestion: string | null;
  spanIds: number[] | null;
}

export interface IReviewSummaryOneLiner {
  id: number;
  summary?: string;
  keywords: OneLinerKeywords[];
}

export interface IReviewSource {
  count: number;
  source: ReviewSource;
}

export type IReviewAspectCount = {
  [key in ReviewAspect]: ISentimentOverview[];
};

export type IReviewSummaries = {
  [key in ReviewAspect]: IReviewSummaryOneLiner[] | IReviewSummary[];
};

export interface IGoogleReviewReply {
  id?: number;
  content: string;
  isDeleteDisabled?: boolean;
}
export interface IReviewResponseType {
  response: {
    success: boolean;
    data: { reviewResponse: IGoogleReviewReply };
    message: string;
  };
  statusCode: number;
}
export interface ISummariesWithCounts {
  aspect: ReviewAspect;
  summaries: IReviewSummary[];
}

export interface IReviewSentimentOverview {
  sentiments: ISentimentOverview[];
  total: number;
}

export interface IReviewAspect {
  aspect: ReviewAspect;
  sentiment: ReviewSentiment;
  expandedSpan: string;
  originalSpan: string;
}

export interface IReviewResponse {
  id: number;
  content: string;
}

export interface IReview {
  id: number;
  cleanReview: string;
  review: string;
  rating?: number;
  createdAt: Date;
  updatedAt: Date;
  username: string;
  sentiment: ReviewSentiment;
  aspectSentiment: ReviewAspectSentiment;
  source: ReviewSource;
  aspects: IReviewAspect[];
  isRead: boolean;
  restaurantId: number;
  response?: IReviewResponse;
  user?: { fullName: string };
  topics?: { name: string; parentId?: number }[];
  sourceDetails?: { [key: string]: string };
  ticketId?: number;
}

export interface ITopic {
  id: number;
  name: string;
  parentName: string;
  sentiment: ReviewSentiment;
  count: number;
  childCount: number;
  positiveCount: number;
  negativeCount: number;
  percentage: number;
}

export interface IReviewAccount {
  id: number;
  placeId: string;
  source: ReviewAccountSource;
  placeDetails?: any;
  status: RecordStatus;
  restaurantId: number;
  businessId?: number;
}

export interface IAiReviewResponse {
  id: number;
  content: string;
  type: ReviewReplyTypes;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IReviewRecommendation {
  id: number;
  recommendation: string;
  keywords: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface IAddAccountRequest {
  source: ReviewSource;
  placeId: string;
  restaurantId: number;
}

export interface IUpdateAccountRequest {
  source: ReviewSource;
  placeId?: string;
  status?: RecordStatus;
  restaurantId: number;
}

export interface IReadReviewRequest {
  reviewId: number;
  restaurantId: number;
}
export interface Ratings {
  rating: number;
  count: number;
  percentage: number;
}
export interface Sources {
  source: ReviewSource;
  rating: number;
  rate: number;
}

export interface Overview {
  total: number;
  average: number;
  ratings: Ratings[];
  sources: Sources[];
}

export interface OverviewResponseType extends BaseResponseType2 {
  response: {
    data: {
      overview?: Overview;
    };
  };
}

export interface OverviewListResponseType extends BaseResponseType2 {
  response: {
    data: {
      reviews?: IReview[];
      total?: number;
    };
  };
}

export interface AllOverviewListResponseType extends BaseResponseType2 {
  response: {
    data: {
      reviews?: IReview[];
      total?: number;
      pagination: {
        totalPages: number;
        nextPage: boolean;
      };
    };
  };
}

export interface ReviewTransform {
  title: string;
  reviews: IReview[];
}

export interface AllOverviewListTransformType extends BaseResponseType2 {
  response: {
    data: {
      reviews?: ReviewTransform[]; // Array of ReviewTransform objects
      total?: number;
    };
  };
}
export interface IReviewAIGeneratedRequest {
  ratings: number;
  restaurantId?: number;
  reviewId: number;
  review: string;
  food_items?: string;
  customer_name: string;
  additional_context?: string;
  previous_replies?: string[];
  length?: ReviewReplyTypes;
  brand_voices: string;
  sentiment: ReviewSentiment;
}
export interface AIGeneratedReplyHistoryPropsType extends BaseResponseType2 {
  response: {
    data: {
      history?: IAiReviewResponse[]; // Array of ReviewTransform objects
      total?: number;
    };
  };
}

export const Months = {
  [ReviewDurations.ALL_TIME]: 1,
  [ReviewDurations.MONTH]: 1,
  [ReviewDurations.HALF_YEAR]: 6,
  [ReviewDurations.YEAR]: 12,
};
